<script lang="ts" setup>
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { MaterialCategorySelector, type CategoryItem } from '@ilteducation/components';
import { getQueryString, SearchInput, useFilters } from '@ilteducation/content-search';
import type { SearchQuery } from '@ilteducation/content-search';
import { useCategories } from '@ilteducation/content-ref-data';
import { MATERIAL_CATEGORY_ALL_ID } from '@/composables/use-all-materials-category';
import useShortcutMaterialCategories from '@/composables/use-shortcut-material-categories';
import { useLocale } from '@/plugins/translation';
import useSubscriptions from '@/foundations/helpers/use-subscriptions';
import CategoryIcon from '@/components/material/CategoryIcon.vue';

const router = useRouter();
const { t, l } = useLocale();
const subscriptions = useSubscriptions();
const { load: loadCategories } = useCategories();
const { removeAllFilters } = useFilters();
const { materialCategories, materialCategoryItems } = useShortcutMaterialCategories();

onMounted(() => {
  removeAllFilters();

  if (subscriptions.value.length) {
    loadCategories(subscriptions.value[0].type);
  }
});

const categoryLink = (category: CategoryItem) => {
  const query =
    category.id === MATERIAL_CATEGORY_ALL_ID
      ? getQueryString({ q: '' })
      : getQueryString({ q: '', filter: { categories: category.id } });
  return `${router.resolve({ name: 'materialsSearch' }).fullPath}${query}`;
};

const onSearch = (searchQuery: SearchQuery) => {
  router.push({ name: 'materialsSearch', query: { q: searchQuery.q } });
};
</script>

<template>
  <MaterialCategorySelector
    :categories="materialCategoryItems"
    :quick-link-section-title="t(l.materials_landing_page.quick_link_section_title)"
    :search-section-title="t(l.materials_landing_page.search_section_title)"
    :category-link="categoryLink"
  >
    <template #quick-link-section-title="{ title }">
      <h2 class="page-title text-center text-secondary mb-4">{{ title }}</h2>
    </template>

    <template #category-image="{ category, size }">
      <CategoryIcon
        :size="size"
        :alt="category.title"
        :category="materialCategories.find((c) => c.id === category.id)"
      />
    </template>

    <template #category-title="{ category }">
      <v-card-title class="text-center text-wrap text-h6 font-weight-medium pa-0">{{ category.title }}</v-card-title>
    </template>

    <template #category-description="{ category }">
      <v-card-subtitle class="text-center text-wrap text-body-2 pa-0">{{ category.description }}</v-card-subtitle>
    </template>

    <template #search-section-title="{ title }">
      <h2 class="section-title text-center font-weight-medium mt-6 mb-2">{{ title }}</h2>
    </template>

    <template #search>
      <SearchInput
        :placeholder="t(l.content_search.label_search_bar)"
        compact
        density="comfortable"
        rounded="lg"
        @submit="onSearch"
      />
    </template>
  </MaterialCategorySelector>
</template>

<style lang="scss" scoped>
.page-title {
  font-size: 2rem;
}

.section-title {
  font-size: 1rem;
}
</style>
